import { Vtype } from '@components/AppNexus/types'
import contextBlickTV from '@contexts/blickTV'
import { useVideo } from '@hooks/useVideo'
import { getLanguage } from '@i18n'
import BrightcoveVideoWeb from '@ringierag/blicktv-web-player'
import {
  AutoplayType,
  BrightcoveVideoWebPlayer,
  DarkCovePlayerWebProps,
  DarkCovePlayerWebPropsWithCarriedOverRef,
} from '@widgets/Video/types/darkcove'
import {
  forwardRef,
  FunctionComponent,
  memo,
  MutableRefObject,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import * as portals from 'react-reverse-portal'
import VideoPoster from './VideoPoster'
import styled, { css } from 'styled-components'
import useIsServerSideRendered from '@hooks/useIsServerSideRendered'

const lang = getLanguage()

const { BlickTVContext } = contextBlickTV

const VideoPlayerWrapper = styled.div`
  ${({
    theme: {
      colors: {
        blicktvGrey: { btvGrey_100 },
      },
    },
  }) =>
    css`
      background-color: ${btvGrey_100};
      padding-top: 56.25%;
      width: 100%;
      position: relative;
    `}
`

const VideoPlayer = styled.div`
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  position: absolute;
`

export const VideoInlineRenderer: FunctionComponent<any> = ({ portalNode }) => {
  return <portals.OutPortal node={portalNode} />
}

export const isVideoReady = (videoId: string): boolean => {
  const allPlayers = window?.videojs?.getAllPlayers?.()

  return Boolean(
    allPlayers?.some(
      (player: any) =>
        (player?.isDAIEnabled_ || player?.mediainfo?.id === videoId) &&
        player?.isReady_ === true
    )
  )
}

export const getIsAutoplay = (autoplayRaw: AutoplayType): boolean =>
  autoplayRaw === 'muted'

export const getIsNonFloatingBlickTVWithAutoplay = (
  isBlickTV: boolean,
  isFloating: boolean,
  isAutoplay: boolean
): boolean => isBlickTV && !isFloating && isAutoplay

export const getIsFloatingBlickTV = (
  isBlickTV: boolean,
  isFloating: boolean
): boolean => isBlickTV && isFloating

export const BrightcoveVideoWebWrapper: FunctionComponent<
  DarkCovePlayerWebPropsWithCarriedOverRef
> = (props) => {
  const getIsSSR = useIsServerSideRendered()

  const { autoplay, isBlickTV, image, ...propsWithoutAutoplay } = props

  const {
    videoId,
    allowFloating,
    playerRef,
    adContainerId,
    mountPlayer,
    isInViewport,
    isInVideoOnlyArticle,
    vtype,
    isMainElement,
  } = propsWithoutAutoplay

  const isAutoplay = getIsAutoplay(autoplay)

  const {
    hasPortalNode,
    portalNode,
    isPoppedOut,
    hasPlayer,
    addPlayer,
    preventAutoMount,
    stopLivePlayback,
    resumeLivePlayback,
    setBlickTVPlayerId,
  } = useContext(BlickTVContext)

  const { isOverlay, hideOverlay } = useVideo()

  const hasVideoAutoStarted = useRef<boolean>(false)
  const autoStartInterval = useRef<ReturnType<typeof setTimeout>>()
  const vjsPlayerId = useRef<string>('')
  const vjsPlayerIdInterval = useRef<ReturnType<typeof setInterval>>()
  const [isVideoPosterClicked, setIsVideoPosterClicked] = useState(false)
  const isLivestreamInArticle = !isBlickTV && vtype === Vtype.LIVESTREAM
  const isFloatingBlickTV = getIsFloatingBlickTV(isBlickTV, allowFloating)
  const isNonFloatingBlickTVWithAutoplay = getIsNonFloatingBlickTVWithAutoplay(
    isBlickTV,
    allowFloating,
    isAutoplay
  )

  const shouldAutoplay: AutoplayType = isNonFloatingBlickTVWithAutoplay
    ? autoplay
    : false

  const propsWithAdjustedAutoplay = {
    ...propsWithoutAutoplay,
    isLive: isBlickTV,
    autoplay: isVideoPosterClicked || shouldAutoplay,
    showPoster: isBlickTV,
    posterURL: isBlickTV ? props.posterURL : '',
    isLivestreamInArticle,
    preferredPeakBitRate: 2750000,
  }

  const isVOD = !isFloatingBlickTV && !isNonFloatingBlickTVWithAutoplay

  const shouldShowPoster = !isVideoPosterClicked && !isBlickTV
  const showVideoInlineRenderer =
    isFloatingBlickTV && hasPortalNode && !isPoppedOut
  const shouldShowBrightcoveVideoWeb =
    mountPlayer && (isNonFloatingBlickTVWithAutoplay || isVOD)

  const onVideoPosterClick = useCallback(() => {
    setIsVideoPosterClicked(true)
  }, [])

  const assignRef = useCallback(
    (ref: any) => {
      playerRef.current = ref

      if (!vjsPlayerId.current && !vjsPlayerIdInterval.current) {
        vjsPlayerIdInterval.current = setInterval(() => {
          const playerId = playerRef.current?.player?.()?.id_
          if (!vjsPlayerId.current && playerId) {
            if (vjsPlayerIdInterval.current) {
              clearInterval(vjsPlayerIdInterval.current)
            }
            vjsPlayerId.current = playerId
            if (isFloatingBlickTV) {
              setBlickTVPlayerId(playerId)
            }
          }
        }, 300)
      }
    },
    [playerRef, isFloatingBlickTV, setBlickTVPlayerId]
  )

  const mountBlickTVFloatingPlayer = useCallback(() => {
    addPlayer(
      <BrightcoveVideoWeb
        lang={lang}
        ref={assignRef}
        suppressNotSupportedError
        {...propsWithoutAutoplay}
        isLive={isBlickTV}
        autoplay={false}
      />,
      playerRef
    )
  }, [addPlayer, isBlickTV, playerRef, propsWithoutAutoplay, assignRef])

  useEffect(() => {
    return () => {
      if (isOverlay) {
        hideOverlay()
      }
    }
  }, [hideOverlay, isOverlay])

  useEffect(() => {
    if (!getIsSSR() && (isInVideoOnlyArticle || isLivestreamInArticle)) {
      setIsVideoPosterClicked(true)
    }
  }, [isInVideoOnlyArticle, isLivestreamInArticle, getIsSSR])

  useEffect(() => {
    if (isFloatingBlickTV) {
      if (hasPortalNode && !hasPlayer) {
        if (mountPlayer) {
          mountBlickTVFloatingPlayer()
        }
      }
    }
  }, [
    isFloatingBlickTV,
    hasPortalNode,
    hasPlayer,
    mountPlayer,
    mountBlickTVFloatingPlayer,
  ])

  useEffect(() => {
    if (isFloatingBlickTV) {
      if (hasPlayer) {
        if (preventAutoMount) {
          if (isInViewport) {
            resumeLivePlayback()
          } else {
            stopLivePlayback()
          }
        } else {
          if (isInViewport && !hasVideoAutoStarted.current) {
            if (isVideoReady(videoId)) {
              hasVideoAutoStarted.current = true
              resumeLivePlayback()
            } else {
              autoStartInterval.current = setInterval(() => {
                if (isVideoReady(videoId)) {
                  if (autoStartInterval.current) {
                    clearInterval(autoStartInterval.current)
                  }
                  hasVideoAutoStarted.current = true
                  resumeLivePlayback()
                }
              }, 16)
            }
          }
        }
      }
    }

    return () => {
      autoStartInterval.current && clearInterval(autoStartInterval.current)
    }
  }, [
    isFloatingBlickTV,
    hasPlayer,
    preventAutoMount,
    autoStartInterval,
    isInViewport,
    hasVideoAutoStarted,
    stopLivePlayback,
    resumeLivePlayback,
    videoId,
  ])

  useEffect(() => {
    return () => {
      if (!isFloatingBlickTV) {
        if (vjsPlayerId.current) {
          try {
            window?.videojs?.getPlayer?.(vjsPlayerId.current)?.dispose?.()
          } catch (err) {
            // Prevent any errors from being logged in Sentry
          }
        }
      }
    }
  }, [isFloatingBlickTV])

  const sharedProps = {
    ...propsWithAdjustedAutoplay,
    ref: assignRef,
    suppressNotSupportedError: true,
    lang,
  }

  return (
    <VideoPlayerWrapper
      key={`${videoId}${
        isBlickTV ? `-blick-tv-${allowFloating ? 'floating' : 'embed'}` : ''
      }`}>
      <VideoPlayer>
        <div id={adContainerId} />
        {shouldShowPoster ? (
          <VideoPoster
            videoId={videoId}
            image={image}
            onClick={onVideoPosterClick}
            isMainElement={isMainElement}
          />
        ) : (
          <>
            {showVideoInlineRenderer ? (
              <VideoInlineRenderer portalNode={portalNode.current} />
            ) : null}
            {shouldShowBrightcoveVideoWeb ? (
              <BrightcoveVideoWeb {...sharedProps} />
            ) : null}
          </>
        )}
      </VideoPlayer>
    </VideoPlayerWrapper>
  )
}

//* NOTE: This component should be never re-rendered because of an issue in:
//* https://github.com/brightcove/react-player-loader
export const MemoizedBrightcoveVideoWebWrapper = memo(
  BrightcoveVideoWebWrapper,
  (currentProps) => currentProps.mountPlayer
)

MemoizedBrightcoveVideoWebWrapper.displayName =
  'MemoizedBrightcoveVideoWebWrapper'

const ForwardRefMemoizedBrightcoveVideoWebWrapper = forwardRef<
  BrightcoveVideoWebPlayer,
  DarkCovePlayerWebProps
>((props, ref) => {
  const { isBlickTV, allowFloating, autoplay, isInViewport, mountPlayer } =
    props
  const isAutoplay = getIsAutoplay(autoplay)
  const isNonFloatingBlickTVWithAutoplay = getIsNonFloatingBlickTVWithAutoplay(
    isBlickTV,
    allowFloating,
    isAutoplay
  )
  const isFloatingBlickTV = getIsFloatingBlickTV(isBlickTV, allowFloating)

  const propsWithCarriedOverRef: DarkCovePlayerWebPropsWithCarriedOverRef = {
    ...props,
    playerRef: ref as MutableRefObject<BrightcoveVideoWebPlayer>,
  }

  useEffect(() => {
    const stopInlineLivePlayback = () => {
      propsWithCarriedOverRef.playerRef.current?.mute?.()
      propsWithCarriedOverRef.playerRef.current?.pause?.()
    }

    const resumeInlineLivePlayback = () => {
      propsWithCarriedOverRef.playerRef.current?.mute?.()
      propsWithCarriedOverRef.playerRef.current?.seekToLive?.()
      propsWithCarriedOverRef.playerRef.current?.play?.()
    }

    if (isNonFloatingBlickTVWithAutoplay) {
      if (mountPlayer && isInViewport !== null) {
        if (isInViewport) {
          resumeInlineLivePlayback()
        } else {
          stopInlineLivePlayback()
        }
      }
    }
  }, [
    isInViewport,
    isNonFloatingBlickTVWithAutoplay,
    mountPlayer,
    propsWithCarriedOverRef.playerRef,
  ])

  if (isFloatingBlickTV) {
    return <BrightcoveVideoWebWrapper {...propsWithCarriedOverRef} />
  }

  return <MemoizedBrightcoveVideoWebWrapper {...propsWithCarriedOverRef} />
})

ForwardRefMemoizedBrightcoveVideoWebWrapper.displayName =
  'ForwardRefMemoizedBrightcoveVideoWebWrapper'

export default ForwardRefMemoizedBrightcoveVideoWebWrapper
